import React, { useState, useRef } from "react";
import { Member, PASSWORD_MIN_STR } from "../../../constants";
import { signUpSteps, errorLocations } from "../authHelpers";
import Media from "react-media";
import { mediaBreakpoints } from "@accessdev/arc-ui-react/utils/js";
import SignUpWizard from "./SignUpWizard";
import { t } from "@lingui/core/macro";

const { REGISTER_WITH_EMAIL, REGISTERING, REGISTER_WITH_CODE } = Member;

const SignUpForm = (props) => {
  const domainsRef = useRef(null);

  const approvedDomains =
    props.registrationType === REGISTER_WITH_EMAIL ? props.approvedDomains : [];
  const [isDomainsFlyoutOpen, setIsDomainsFlyoutOpen] = useState(false);
  const [domains] = useState(approvedDomains);
  const hasDomains = approvedDomains.length > 0;
  const hasMultipleDomains = approvedDomains.length > 1;
  const [selectedDomain, setSelectedDomain] = useState(() => {
    const email = props.values?.email || "";
    const domain = email.substring(email.lastIndexOf("@"));
    return (
      approvedDomains.find((approvedDomain) => approvedDomain === domain) ||
      approvedDomains[0] ||
      ""
    );
  });
  const [token, setToken] = useState(null);

  const showError = (input) => {
    const { error, codeErrorMsg, memberKeyLabel } = props;
    if (error && error.location === input) {
      switch (input) {
        case errorLocations.SIGN_UP_MEMBERID:
        case errorLocations.SIGN_UP_REGISTRATION_CODE:
          return error.errorMsg || codeErrorMsg;
        default:
          return error.errorMsg;
      }
    } else {
      switch (input) {
        case errorLocations.SIGN_UP_EMAIL:
          return t`Please enter a valid email`;
        case errorLocations.SIGN_UP_MEMBERID:
          return memberKeyLabel ? t`${memberKeyLabel} is required.` : t`Member ID is required`;
        case errorLocations.SIGN_UP_REGISTRATION_CODE:
          return memberKeyLabel
            ? t`${memberKeyLabel} is required.`
            : t`Registration code is required.`;
        default:
          return "";
      }
    }
  };

  const openDomainsFlyout = () => {
    if (hasMultipleDomains) {
      props.setSignUpStep(signUpSteps.EMAIL);
      setIsDomainsFlyoutOpen(true);
    }
  };

  const closeDomainsFlyout = () => {
    setIsDomainsFlyoutOpen(false);
  };

  const selectDomain = (domain) => {
    closeDomainsFlyout();
    setSelectedDomain(domain);
    onDomainValueChange(domain);
    if (domainsRef.current) domainsRef.current.focus();
  };

  const onDomainValueChange = React.useCallback(
    (domain) => {
      props.onValueChange({
        target: { name: "email_domain", value: domain },
      });
    },
    [props],
  );

  const onEmailValueChange = (e) => {
    if (!hasDomains || alphaNumericValidator(e.target.value)) {
      props.onValueChange(e);
    } else {
      onDomainValueChange(selectedDomain);
    }
  };

  const onVerify = (token) => {
    setToken(token);
    props.onCaptchaChange(token);
  };

  const isButtonDisabled = (isPhone) => {
    if (isPhone) {
      return props.registration.status === REGISTERING;
    }

    if (props.registrationType === REGISTER_WITH_CODE) {
      return props.registration.status === REGISTERING || props.captchaToken === null;
    }

    return props.registration.status === REGISTERING || props.values.passwordStr < PASSWORD_MIN_STR;
  };

  return (
    <Media query={mediaBreakpoints.forPhoneOnly}>
      {(isPhone) => (
        <SignUpWizard
          {...props}
          isDomainsFlyoutOpen={isDomainsFlyoutOpen}
          domains={domains}
          hasDomains={hasDomains}
          hasMultipleDomains={hasMultipleDomains}
          selectedDomain={selectedDomain}
          token={token}
          showError={showError}
          onEmailValueChange={onEmailValueChange}
          openDomainsFlyout={openDomainsFlyout}
          closeDomainsFlyout={closeDomainsFlyout}
          selectDomain={selectDomain}
          isCompleteProfile={props.isCompleteProfile}
          completeSignUp={props.completeSignUp}
          onCaptchaVerify={onVerify}
          captchaToken={token}
          isPhone={isPhone}
          isButtonDisabled={isButtonDisabled}
        />
      )}
    </Media>
  );
};

export default SignUpForm;

export const alphaNumericValidator = (value) => {
  if (!value) {
    return true;
  }

  const alphaNumRegex = new RegExp("^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$");

  if (alphaNumRegex.test(value)) {
    return true;
  }

  return false;
};
